import React, { useEffect, useRef, useState } from "react";
import { ScanLine, Camera, Plus, Gift, Loader2, User } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { printWalkInReceipt } from "@/lib/receipt";

const REGION_ID = "metros-qr-region";
const PREFIX = "METROS-LOYALTY:";

const pushDisplay = (c, extra = {}) => {
  if (!c) return;
  api.post("/admin/display/event", { customer_name: c.name || c.email || "Customer", points: c.loyalty_points, stage: "scanned", ...extra }).catch((e) => console.warn("display event failed", e));
};

export default function AdminScanner() {
  const [scanning, setScanning] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [settings, setSettings] = useState(null);
  const [manual, setManual] = useState("");
  const [amount, setAmount] = useState("");
  const [redeemPts, setRedeemPts] = useState("");
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const lookupLock = useRef(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { setSettings(data || null); setRewards(data?.rewards || null); }).catch(() => {});
  }, []);

  const ppp = rewards ? (rewards.points_per_pound || 0) : 0;
  const perPoint = rewards && rewards.reward_threshold ? (rewards.reward_value || 0) / rewards.reward_threshold : 0.05;

  const stopScanner = async () => {
    const s = scannerRef.current;
    if (s) {
      try { await s.stop(); } catch { /* already stopped */ }
      try { await s.clear(); } catch { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };
  useEffect(() => () => { stopScanner(); }, []);

  const lookup = async (raw) => {
    if (lookupLock.current) return;
    const code = String(raw || "").trim();
    const email = code.startsWith(PREFIX) ? code.slice(PREFIX.length).trim() : code;
    if (!email) { toast.error("Not a valid loyalty code"); return; }
    lookupLock.current = true;
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/customers?q=${encodeURIComponent(email)}`);
      const match = (data || []).find((c) => (c.email || "").toLowerCase() === email.toLowerCase() || (c.loyalty_code || "").toUpperCase() === email.toUpperCase()) || (data || [])[0];
      if (!match) { toast.error("No customer found for that code"); return; }
      await stopScanner();
      setCustomer(match); setAmount(""); setRedeemPts(""); setManual("");
      pushDisplay(match);
    } catch { toast.error("Lookup failed"); }
    finally { setBusy(false); lookupLock.current = false; }
  };

  const startScanner = async () => {
    setCustomer(null);
    setScanning(true);
    setTimeout(async () => {
      try {
        const inst = new Html5Qrcode(REGION_ID);
        scannerRef.current = inst;
        await inst.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 }, (decoded) => lookup(decoded), () => {});
      } catch {
        toast.error("Could not access camera. Enter the code manually below.");
        setScanning(false);
      }
    }, 150);
  };

  const addPoints = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter the order amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/customers/${customer.id}/earn`, { amount: amt });
      setCustomer((c) => ({ ...c, loyalty_points: data.loyalty_points }));
      pushDisplay({ ...customer, loyalty_points: data.loyalty_points }, { stage: "done", points_added: data.points_added, amount: data.amount });
      setAmount("");
      toast.success(`Added ${data.points_added} pts · new balance ${data.loyalty_points}`);
      printWalkInReceipt({ type: "earn", customerName: customer.name || customer.email, amount: data.amount, points: data.points_added, balance: data.loyalty_points }, settings);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add points"); }
    finally { setBusy(false); }
  };

  const redeem = async () => {
    const pts = parseInt(redeemPts, 10);
    if (!pts || pts <= 0) { toast.error("Enter points to redeem"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/customers/${customer.id}/redeem`, { points: pts });
      setCustomer((c) => ({ ...c, loyalty_points: data.loyalty_points }));
      pushDisplay({ ...customer, loyalty_points: data.loyalty_points }, { stage: "done", points_redeemed: data.points_redeemed, discount: data.value });
      setRedeemPts("");
      toast.success(`Redeemed ${data.points_redeemed} pts = ${money(data.value)} · new balance ${data.loyalty_points}`);
      printWalkInReceipt({ type: "redeem", customerName: customer.name || customer.email, points: data.points_redeemed, value: data.value, balance: data.loyalty_points }, settings);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not redeem points"); }
    finally { setBusy(false); }
  };

  const earnPreview = amount && parseFloat(amount) > 0 ? Math.round(parseFloat(amount) * ppp) : 0;
  const redeemPreview = redeemPts && parseInt(redeemPts, 10) > 0 ? parseInt(redeemPts, 10) * perPoint : 0;

  return (
    <div className="space-y-5 max-w-xl" data-testid="admin-scanner">
      <div>
        <h1 className="font-heading font-extrabold text-3xl text-metros-black flex items-center gap-2"><ScanLine className="text-metros-red" /> Scan / Walk-in</h1>
        <p className="text-sm text-muted-foreground mt-1">Scan a customer's loyalty QR code to add or redeem points for an in-person order.</p>
      </div>

      {!customer ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
          {scanning ? (
            <>
              <div id={REGION_ID} data-testid="scanner-region" className="w-full rounded-xl overflow-hidden bg-black min-h-[240px]" />
              <button data-testid="stop-scan-btn" onClick={stopScanner} className="w-full border border-gray-200 rounded-full py-3 font-bold text-sm">Stop camera</button>
            </>
          ) : (
            <button data-testid="start-scan-btn" onClick={startScanner} className="w-full bg-metros-red text-white rounded-full py-4 font-bold flex items-center justify-center gap-2 active:scale-95 transition-all">
              <Camera size={18} /> Scan customer QR code
            </button>
          )}

          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs text-muted-foreground mb-2">Or enter the customer's loyalty code manually:</p>
            <form onSubmit={(e) => { e.preventDefault(); lookup(manual); }} className="flex gap-2">
              <input data-testid="manual-code-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="METROS-LOYALTY:email@…" className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-metros-red" />
              <button type="submit" data-testid="manual-lookup-btn" disabled={busy} className="bg-metros-black text-white rounded-xl px-5 text-sm font-bold disabled:opacity-50">Find</button>
            </form>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-metros-black text-white rounded-2xl p-5 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-white/10 rounded-full p-2.5"><User size={20} /></div>
              <div className="min-w-0">
                <p data-testid="scanned-customer-name" className="font-heading font-extrabold text-lg truncate">{customer.name || "Customer"}</p>
                <p className="text-xs text-gray-400 truncate">{customer.email}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[11px] text-gray-400">Balance</p>
              <p data-testid="scanned-customer-points" className="font-heading font-extrabold text-2xl text-metros-red">{customer.loyalty_points} pts</p>
            </div>
          </div>

          {/* Add points */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-3"><Plus size={18} className="text-metros-red" /> Add points</h2>
            <label className="text-xs text-muted-foreground">Order amount (£)</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="add-amount-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 24.99" className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-metros-red" />
              <button data-testid="add-points-btn" onClick={addPoints} disabled={busy} className="bg-metros-red text-white rounded-xl px-5 text-sm font-bold disabled:opacity-50">Add</button>
            </div>
            <p data-testid="add-points-preview" className="text-xs text-muted-foreground mt-2">Will add <b className="text-metros-black">{earnPreview} pts</b> ({ppp} pts per £1)</p>
          </div>

          {/* Redeem points */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-3"><Gift size={18} className="text-metros-red" /> Redeem points</h2>
            <label className="text-xs text-muted-foreground">Points to redeem</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="redeem-points-input" type="number" min="0" step="1" value={redeemPts} onChange={(e) => setRedeemPts(e.target.value)} placeholder="e.g. 100" className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-metros-red" />
              <button data-testid="redeem-points-btn" onClick={redeem} disabled={busy} className="bg-metros-black text-white rounded-xl px-5 text-sm font-bold disabled:opacity-50">Redeem</button>
            </div>
            <p data-testid="redeem-value-preview" className="text-xs text-muted-foreground mt-2">Worth <b className="text-metros-black">{money(redeemPreview)}</b> off the order</p>
          </div>

          <button data-testid="scan-another-btn" onClick={startScanner} className="w-full border border-gray-200 rounded-full py-3 font-bold text-sm flex items-center justify-center gap-2"><ScanLine size={16} /> Scan another customer</button>
        </div>
      )}
      {busy && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={14} /> Working…</div>}
    </div>
  );
}
